import { plain } from '../../lib/labels';
import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import {
  ReloadOutlined,
  StopOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { userLabel, userSecondary, userText } from '../../lib/listenerName';

interface Occupant {
  position: number;
  userId: string;
  handle: string;
  /** The occupant's own Display Name — absent on older APIs. */
  displayName?: string | null;
  isHost: boolean;
  isMuted: boolean;
  totalGiftPaise: string;
}

interface PartyRoom {
  id: string;
  title: string;
  status: 'live' | 'disconnected' | 'ended_normal' | 'ended_flagged' | 'ended_dropped';
  hostUserId: string;
  hostHandle: string;
  hostDisplayName?: string | null;
  seatCount: number;
  seatsFilled: number;
  viewerCount: number;
  viewerPeak: number;
  totalGiftPaise: string;
  startedAt: string;
  hostLastSeenAt: string | null;
  occupants: Occupant[];
}

interface PartyLog {
  id: string;
  roomId: string;
  hostUserId: string;
  eventType: string;
  adminId: string | null;
  reason: string | null;
  metadata: unknown;
  createdAt: string;
}

interface PartySettings {
  partyEnabled: boolean;
  reconnectTimeoutSec: number;
  maxSimultaneousRooms: number;
}

function errMsg(e: unknown, fallback: string) {
  return (
    (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback
  );
}

function rupees(paise: string) {
  return `₹${(Number(paise) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function since(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// ---------------------------------------------------------------------------

function RoomsTab() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [closing, setClosing] = useState<PartyRoom | null>(null);
  const [reason, setReason] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'party', 'rooms'],
    queryFn: async () => (await api.get<{ rooms: PartyRoom[] }>('/admin/party/rooms')).data.rooms,
    // Parties are short-lived; a stale list is worse than a few extra requests.
    refetchInterval: 10_000,
  });

  const forceEnd = useMutation({
    mutationFn: () => api.post(`/admin/party/${closing?.id}/force-end`, { reason: reason.trim() }),
    onSuccess: () => {
      message.success('Party closed');
      setClosing(null);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['admin', 'party'] });
    },
    onError: (e) => message.error(errMsg(e, 'Could not close the party')),
  });

  const columns: TableColumnsType<PartyRoom> = [
    {
      title: 'Party',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{row.title || '(untitled)'}</span>
          <span style={{ fontSize: 11, color: '#8B8B8B' }}>
            host {userText({ displayName: row.hostDisplayName, handle: row.hostHandle })} · started {since(row.startedAt)} ago
          </span>
        </Space>
      ),
    },
    {
      title: 'Status',
      width: 130,
      render: (_: unknown, row) =>
        row.status === 'live' ? (
          <Tag color="success">live</Tag>
        ) : row.status === 'disconnected' ? (
          <Tooltip title="The host's app stopped reporting in. The room is hidden from discovery and will auto-close if they don't come back.">
            <Tag color="warning" icon={<WarningOutlined />}>
              reconnecting
            </Tag>
          </Tooltip>
        ) : (
          <Tag>{plain(row.status)}</Tag>
        ),
    },
    {
      title: 'Seats',
      width: 200,
      render: (_: unknown, row) => (
        <Space direction="vertical" size={2}>
          <Tag icon={<TeamOutlined />}>
            {row.seatsFilled} / {row.seatCount}
          </Tag>
          <Space size={4} wrap>
            {row.occupants.map((o) => (
              <Tag key={o.position} color={o.isHost ? 'purple' : undefined}>
                {o.isHost ? '★ ' : ''}
                {[userLabel(o), userSecondary(o)].filter(Boolean).join(' · ')}
                {o.isMuted ? ' 🔇' : ''}
              </Tag>
            ))}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Audience',
      width: 110,
      render: (_: unknown, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.viewerCount} now</span>
          <span style={{ fontSize: 11, color: '#8B8B8B' }}>peak {row.viewerPeak}</span>
        </Space>
      ),
    },
    {
      title: 'Gifts',
      width: 110,
      render: (_: unknown, row) => <Tag color="gold">{rupees(row.totalGiftPaise)}</Tag>,
    },
    {
      title: 'Actions',
      width: 130,
      render: (_: unknown, row) => (
        <Can permission="party.force_end">
          <Button
            size="small"
            danger
            icon={<StopOutlined />}
            onClick={() => {
              setClosing(row);
              setReason('');
            }}
          >
            Force close
          </Button>
        </Can>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button icon={<ReloadOutlined />} onClick={() => void refetch()}>
          Refresh
        </Button>
      </div>
      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={data ?? []}
        columns={columns}
        pagination={{ pageSize: 25 }}
        locale={{ emptyText: 'No parties are open right now.' }}
      />

      <Modal
        title={`Force close — ${closing?.title || '(untitled)'}`}
        open={!!closing}
        onCancel={() => setClosing(null)}
        onOk={() => forceEnd.mutate()}
        okText="Close the party"
        okButtonProps={{ danger: true, disabled: reason.trim().length < 3, loading: forceEnd.isPending }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            showIcon
            type="warning"
            message="Everyone is removed immediately"
            description="Seated participants and the audience are all dropped. Gifts already sent are not reversed — the ledger is not rewound by a close."
          />
          <div>
            <Typography.Text>Reason (recorded against you)</Typography.Text>
            <Input.TextArea
              rows={3}
              style={{ marginTop: 4 }}
              placeholder="Why is this party being closed?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </Space>
      </Modal>
    </Space>
  );
}

// ---------------------------------------------------------------------------

function LogsTab() {
  const [roomId, setRoomId] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'party', 'logs', roomId],
    queryFn: async () =>
      (
        await api.get<{ items: PartyLog[] }>('/admin/party/logs', {
          params: roomId ? { roomId, limit: 100 } : { limit: 100 },
        })
      ).data.items,
  });

  const columns: TableColumnsType<PartyLog> = [
    {
      title: 'When',
      width: 180,
      render: (_: unknown, row) => new Date(row.createdAt).toLocaleString('en-IN'),
    },
    {
      title: 'Event',
      width: 190,
      render: (_: unknown, row) => <Tag>{plain(row.eventType)}</Tag>,
    },
    {
      title: 'Room',
      width: 130,
      render: (_: unknown, row) => <code style={{ fontSize: 11 }}>{row.roomId ? row.roomId.slice(0, 8) : '—'}</code>,
    },
    {
      title: 'Detail',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={0}>
          {row.reason && <span>{row.reason}</span>}
          {row.adminId && (
            <span style={{ fontSize: 11, color: '#C58B00' }}>
              by admin {row.adminId.slice(0, 8)}
            </span>
          )}
          {!!row.metadata && (
            <code style={{ fontSize: 11, color: '#8B8B8B' }}>{JSON.stringify(row.metadata)}</code>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Input
        allowClear
        style={{ maxWidth: 380 }}
        placeholder="Filter by room id (leave blank for everything)"
        value={roomId}
        onChange={(e) => setRoomId(e.target.value.trim())}
      />
      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={data ?? []}
        columns={columns}
        pagination={{ pageSize: 50 }}
      />
    </Space>
  );
}

// ---------------------------------------------------------------------------

function SettingsTab() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [draft, setDraft] = useState<PartySettings | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'party', 'settings'],
    queryFn: async () => (await api.get<PartySettings>('/admin/party/settings')).data,
  });

  const current = draft ?? data ?? null;

  const save = useMutation({
    mutationFn: () => api.post('/admin/party/settings', current),
    onSuccess: () => {
      message.success('Settings applied — no deploy needed');
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'party', 'settings'] });
    },
    onError: (e) => message.error(errMsg(e, 'Could not save settings')),
  });

  if (isLoading || !current) return <Card loading />;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 560 }}>
      <Alert
        showIcon
        type="info"
        message="There is no eligibility gate on parties"
        description="Anyone can host a party and anyone can take an open seat — there is no level, tier or role requirement, by design. The only controls are the ones below."
      />
      <Card>
        <Space direction="vertical" size={18} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Typography.Text strong>Parties enabled</Typography.Text>
              <div style={{ fontSize: 12, color: '#8B8B8B' }}>
                Off means nobody can open a new party. Rooms already running are not closed.
              </div>
            </div>
            <Switch
              checked={current.partyEnabled}
              onChange={(v) => setDraft({ ...current, partyEnabled: v })}
            />
          </div>

          <div>
            <Typography.Text strong>Host reconnect window (seconds)</Typography.Text>
            <div style={{ fontSize: 12, color: '#8B8B8B', marginBottom: 4 }}>
              How long a host whose app went dark has to come back before the room auto-closes.
              10–60.
            </div>
            <InputNumber
              min={10}
              max={60}
              style={{ width: '100%' }}
              value={current.reconnectTimeoutSec}
              onChange={(v) => setDraft({ ...current, reconnectTimeoutSec: v ?? 10 })}
            />
          </div>

          <div>
            <Typography.Text strong>Max concurrent parties</Typography.Text>
            <div style={{ fontSize: 12, color: '#8B8B8B', marginBottom: 4 }}>
              0 means unlimited.
            </div>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={current.maxSimultaneousRooms}
              onChange={(v) => setDraft({ ...current, maxSimultaneousRooms: v ?? 0 })}
            />
          </div>

          <Can permission="party.settings.edit">
            <Button
              type="primary"
              disabled={!draft}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </Can>
        </Space>
      </Card>
    </Space>
  );
}

// ---------------------------------------------------------------------------

export function PartyPage() {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Parties
      </Typography.Title>
      <Alert
        showIcon
        type="info"
        message="A party is a hosted room with seats"
        description="A host plus one or two seated participants, and an audience watching them together. Viewers pay nothing to enter; the money is in gifts, which are aimed at a specific seat; the listener earns the per-gift ₹ amount set in the Gift catalog."
      />
      <Card>
        <Tabs
          items={[
            { key: 'rooms', label: 'Open parties', children: <RoomsTab /> },
            { key: 'logs', label: 'Room log', children: <LogsTab /> },
            { key: 'settings', label: 'Settings', children: <SettingsTab /> },
          ]}
        />
      </Card>
    </Space>
  );
}
